import { Observable, Subscription } from 'rxjs';

import { InterviewSession, VJFPUser } from './API';
import { ChangeInterviewSessionPubsub, ChangeInvitePubsub, ChangeQueueUserPubsub, CreateMessagePubsub, UpdatePresencePubsub } from './app-events';
import { RxSubscription } from './RxSubscriptions';
import { VJFPCache } from './services/CacheUtils';
import { CGOInviteAppModel, ChangeEvent } from './types';

const trace = (msg: unknown) => {
  if (localStorage.getItem('debug_trace') === 'true') {
    console.log(JSON.stringify(msg));
  }
};

type VJFPSubscription = {
  key: string;
  value: Subscription;
  refCount: number;
  onDispose?: () => void;
  dispose: () => void;
};

const listOfSubscriptions: VJFPSubscription[] = [];

function subscribeAndHandle<T> (
  key: string,
  observable: Observable<T>,
  callback: (value: T) => void,
  onDispose?: () => void
): VJFPSubscription {
  const subscriptionKey = key;
  const existingSubscription = listOfSubscriptions.find(x => x.key === subscriptionKey);

  if (existingSubscription) {
    existingSubscription.refCount += 1;
    return {
      ...existingSubscription,
      dispose: () => disposeSubscription(existingSubscription)
    };
  }

  const subscription = observable.subscribe((value: T) => {
    if (!value) {
      return;
    }
    callback(value);
  });

  const newSubscription: VJFPSubscription = {
    key: subscriptionKey,
    value: subscription,
    refCount: 1,
    onDispose,
    dispose: () => disposeSubscription(newSubscription)
  };

  listOfSubscriptions.push(newSubscription);

  return newSubscription;
}

function disposeSubscription (subscription: VJFPSubscription) {
  if (subscription.refCount > 1) {
    subscription.refCount -= 1;
    return;
  }

  const index = listOfSubscriptions.findIndex(x => x.key === subscription.key);
  if (index !== -1) {
    listOfSubscriptions.splice(index, 1);
    subscription.value.unsubscribe();
    subscription.onDispose?.();
    console.log(`${subscription.key} - unsubscribed`);
  }
}

export function setupQueueSubscriptionsByBoothId (boothId: string) {
  RxSubscription.subscribe('onChangeQueueUserByBoothId', { boothId });

  return subscribeAndHandle(
    `CREATE_QUEUE_USER_BOOTH_${boothId}`,
    RxSubscription.onChangeQueueUserByBoothIdObservable(),
    value => {
      if (value) { ChangeQueueUserPubsub.onAmplifyData(value, null); }
    },
    () => RxSubscription.unsubscribe('onChangeQueueUserByBoothId', { boothId })
  );
}

export function setupQueueSubscriptionsByCandidateId (candidateId: number) {
  RxSubscription.subscribe('onChangeQueueUserByCandidateId', { candidateId });

  return subscribeAndHandle(
    `CREATE_QUEUE_USER_CANDIDATE_${candidateId}`,
    RxSubscription.onChangeQueueUserByCandidateIdObservable(),
    value => ChangeQueueUserPubsub.onAmplifyData(value, null),
    () => RxSubscription.unsubscribe('onChangeQueueUserByCandidateId', { candidateId })
  );
}

export function setupAmplifyPresenceSubscriptions (userIds: Array<number>) {
  trace({ component: 'amplify-subscriptions', event: 'setupAmplifyPresenceSubscriptions', userIds });
  const disposables = userIds.map(userId => {
    trace({ component: 'amplify-subscriptions', event: 'subscribe_presence', userId });
    RxSubscription.subscribe('onUpdateVJFPUserById', { userId });

    const presenceSubscriptionKey = `PRESENCE_${userId}`;

    return subscribeAndHandle(
      presenceSubscriptionKey,
      RxSubscription.onUpdateVJFPUserByIdObservable(),
      value => {
        if (Number(value?.id) !== Number(userId)) return;
        trace({ component: 'amplify-subscriptions', event: 'presence_update_received', userId: value?.id, presenceStatus: value?.presenceStatus, isAvailableForChat: value?.isAvailableForChat });
        const lastCachedUserInfo = VJFPCache.getItem(`vjfpUser-${value.id}`) as VJFPUser;
        if (
          lastCachedUserInfo &&
          lastCachedUserInfo.presenceStatus === value.presenceStatus &&
          lastCachedUserInfo.isAvailableForChat === value.isAvailableForChat
        ) {
          trace({ component: 'amplify-subscriptions', event: 'presence_update_skipped_cached', userId: value?.id, cachedPresence: lastCachedUserInfo.presenceStatus });
          return;
        }

        trace({ component: 'amplify-subscriptions', event: 'presence_update_applied', userId: value?.id, presenceStatus: value?.presenceStatus });
        VJFPCache.setItem(`vjfpUser-${value.id}`, value);
        UpdatePresencePubsub.onAmplifyData(value, null);
      },
      () => RxSubscription.unsubscribe('onUpdateVJFPUserById', { userId })
    );
  });

  return {
    dispose: () => {
      disposables.forEach(disposable => disposable.dispose());
    }
  };
}

export function setupInviteSubscriptions (candidateId: number) {
  const inviteSubscriptionKey = `INVITE_${candidateId}`;
  RxSubscription.subscribe('onChangeInviteByCandidateId', { candidateId });

  return subscribeAndHandle<CGOInviteAppModel>(
    inviteSubscriptionKey,
    RxSubscription.onChangeInviteByCandidateIdObservable(),
    value => ChangeInvitePubsub.onAmplifyData(value, null),
    () => RxSubscription.unsubscribe('onChangeInviteByCandidateId', { candidateId })
  );
}

export function setupAmplifyMessageSubscription (topicId: string) {
  const createMessageSubscriptionKey = `CREATE_MESSAGE_${topicId}`;
  RxSubscription.subscribe('onCreateMessageByTopicId', { topicId });

  return subscribeAndHandle(
    createMessageSubscriptionKey,
    RxSubscription.onCreateMessageByTopicIdObservable(),
    value => {
      if (value !== null) {
        CreateMessagePubsub.onAmplifyData(value, null);
      }
    },
    () => RxSubscription.unsubscribe('onCreateMessageByTopicId', { topicId })
  );
}

export function setupInterviewSessionSubscriptionsByCandidateId (candidateId: number) {
  const changeInterviewSessionByCandidateIdSubscriptionKey = `changeInterviewSessionByCandidateId_${candidateId}`;
  RxSubscription.subscribe('onChangeInterviewSessionByCandidateId', { candidateId });

  return subscribeAndHandle(
    changeInterviewSessionByCandidateIdSubscriptionKey,
    RxSubscription.onChangeInterviewSessionByCandidateIdObservable(),
    (value: ChangeEvent<InterviewSession> | null) => {
      if (value !== null) {
        ChangeInterviewSessionPubsub.onAmplifyData(value, null);
      }
    },
    () => RxSubscription.unsubscribe('onChangeInterviewSessionByCandidateId', { candidateId })
  );
}

export function setupInterviewSessionSubscriptionsByRecruiterId (recruiterId: number) {
  const changeInterviewSessionByRecruiterIdSubscriptionKey = `changeInterviewSessionByRecruiterId_${recruiterId}`;
  RxSubscription.subscribe('onChangeInterviewSessionByRecruiterId', { recruiterId });

  return subscribeAndHandle(
    changeInterviewSessionByRecruiterIdSubscriptionKey,
    RxSubscription.onChangeInterviewSessionByRecruiterIdObservable(),
    (value: ChangeEvent<InterviewSession> | null) => {
      if (value !== null) {
        ChangeInterviewSessionPubsub.onAmplifyData(value, null);
      }
    },
    () => RxSubscription.unsubscribe('onChangeInterviewSessionByRecruiterId', { recruiterId })
  );
}

export function clearAllAmplifySubscriptions () {
  while (listOfSubscriptions.length) {
    listOfSubscriptions[0].dispose();
  }
}
